import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import LanguageToggle from './components/LanguageToggle';
import LoginForm from './components/LoginForm';
import SignUpForm from './components/SignUpForm';
import TrustBadges from './components/TrustBadges';
import Image from '../../components/AppImage';

const Login = () => {
  const { t, i18n } = useTranslation();
  const [authView, setAuthView] = useState('login');
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const lng = i18n.language?.toLowerCase() || 'fr';
    return lng === 'fr' ? 'FR' : lng === 'es' ? 'ES' : lng === 'pl' ? 'PL' : 'EN';
  });

  useEffect(() => {
    let saved = null;
    try {
      saved = localStorage.getItem('appLanguage');
    } catch (_) {}
    const lang = saved || i18n?.language;
    const code = (lang?.toLowerCase() || 'fr').slice(0, 2);
    if (['fr', 'en', 'es', 'pl'].includes(code)) {
      if (typeof i18n?.changeLanguage === 'function') {
        i18n.changeLanguage(code);
      }
      setCurrentLanguage(code === 'fr' ? 'FR' : code === 'es' ? 'ES' : code === 'pl' ? 'PL' : 'EN');
    }
  }, [i18n]);

  const handleLanguageChange = (language) => {
    const code = language?.toLowerCase().slice(0, 2) || 'fr';
    i18n.changeLanguage(code);
    setCurrentLanguage(language);
  };

  const handleLoginSuccess = (user) => {
    console.log('Login successful:', user);
  };

  return (
    <>
      <Helmet>
        <title>{t('loginPage.title')}</title>
        <meta name="description" content="Secure authentication for orthodontic clinic management system" />
      </Helmet>

      <div className="min-h-screen bg-background flex">
        <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-gradient-to-br from-muted via-muted to-background">
          <div className="absolute inset-0 flex items-center justify-center p-12">
            <div className="max-w-lg">
              <div className="mb-8">
                <h2 className="text-4xl font-semibold text-foreground mb-4">
                  {t('loginPage.tagline')}
                </h2>
                <p className="text-lg text-muted-foreground">
                  {t('loginPage.description')}
                </p>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <Image
                  src="https://images.unsplash.com/photo-1643660527076-726d42bb1a06"
                  alt="Modern dental clinic interior with advanced orthodontic equipment, clean white surfaces, and professional medical workspace setup"
                  className="w-full h-auto object-cover" />

              </div>
            </div>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-muted rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-96 h-96 bg-muted rounded-full blur-3xl"></div>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
          <div className="w-full max-w-md">
            <LanguageToggle
              currentLanguage={currentLanguage}
              onLanguageChange={handleLanguageChange} />

            <div className="bg-card rounded-2xl shadow-card p-8 border border-border">
              {authView === 'login' ? (
                <LoginForm
                  language={currentLanguage}
                  onLoginSuccess={handleLoginSuccess}
                  onSignUp={() => setAuthView('signup')}
                />
              ) : (
                <SignUpForm onBackToLogin={() => setAuthView('login')} />
              )}

              <TrustBadges language={currentLanguage} />

              <p className="mt-4 text-center text-[11px] text-muted-foreground">
                v{__APP_VERSION__}
              </p>
            </div>

            <div className="mt-6 text-center">
              <p className="text-xs text-muted-foreground">
                {t('loginPage.copyright')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default Login;